/**
 * Wins (SPEC.md §8.4) — self-contained the way `AreasCard`/`RecurringCard` are.
 *
 * `GET /api/wins?range=` already returns every range's counts alongside the requested
 * range's completions in one call (`services/wins.py`'s `get_wins`) — the same call
 * `build_briefing` was making internally for "today" and handing down as `data.wins`. This
 * card just makes that call itself, on mount and whenever the range switches, instead of
 * riding a page-level fetch. Unlike Areas/Projects it never mutates anything of its own —
 * hovering a win only fires fireworks — so there is no `onChange` to report, only
 * `refreshKey` to catch a completion made elsewhere.
 */

import { useCallback, useEffect, useState } from "react";
import { ApiError, api, type Wins as WinsData, type WinRange } from "../api";
import { WinCounts, WinsList } from "./WinsList";

const WIN_RANGE_LABELS: Record<WinRange, string> = {
  today: "Today",
  yesterday: "Yesterday",
  "7d": "Last 7 days",
  "30d": "Last 30 days",
};

export function WinsCard({
  refreshKey,
}: {
  /** Bumped whenever a task was completed or undone elsewhere, so the counts and — for
   * "today" — the list itself stay current without this card polling. */
  refreshKey?: number;
}) {
  const [range, setRange] = useState<WinRange>("today");
  const [data, setData] = useState<WinsData | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (r: WinRange) => {
    try {
      setData(await api.wins(r));
      setError(null);
    } catch (err) {
      if (err instanceof ApiError && err.isAuth) return; // AuthGate takes over
      setError(err instanceof Error ? err.message : String(err));
    }
  }, []);

  // Refetches on a range switch and whenever `refreshKey` bumps, so a completion made
  // elsewhere refreshes whichever range happens to be on screen.
  useEffect(() => {
    void load(range);
  }, [load, range, refreshKey]);

  return (
    <section className="panel">
      <header>
        <h2>Wins</h2>
        <span className="muted small">
          {range === "today" ? "Today" : WIN_RANGE_LABELS[range]}
        </span>
      </header>

      {error && (
        <p className="error" style={{ margin: "0 0 0.6rem" }}>
          {error}
        </p>
      )}

      <WinCounts counts={data?.counts ?? {}} onSelect={setRange} selected={range} />
      <WinsList
        completions={data?.completions ?? []}
        empty={
          range === "yesterday"
            ? "Nothing yesterday. Today is a separate day."
            : "Nothing yet. Tick one thing off and it lands here."
        }
      />
    </section>
  );
}
